import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { axiosPrivate } from "../../../../api/axios";
import ConfigListPanel from "./ConfigListPanel";
import BuilderPanel from "./BuilderPanel";
import JsonEditorPanel from "./JsonEditorPanel";
import PreviewPanel from "./PreviewPanel";
import DataSourceDrawer from "./DataSourceDrawer";

const TEMPLATE_JSON = {
  sections: [
    {
      id: "new_section",
      title: "새 섹션",
      icon: "Settings",
      widgets: []
    }
  ]
};

export default function MachineJsonEdit() {
  const { group_code, project_code } = useParams();

  // Tailwind CDN Dynamic Loading
  const [isTailwindReady, setIsTailwindReady] = useState(false);
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.tailwindcss.com";
    script.id = "tailwind-cdn-script";
    script.onload = () => {
      setTimeout(() => setIsTailwindReady(true), 100);
    };
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById("tailwind-cdn-script");
      if (existingScript) existingScript.remove();
      const tailwindStyles = document.querySelectorAll('style');
      tailwindStyles.forEach(style => {
        if (style.textContent.includes('--tw-') || style.textContent.includes('tailwindcss')) {
          style.remove();
        }
      });
    };
  }, []);

  // State Management
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [editorValue, setEditorValue] = useState("");
  const [parsedConfig, setParsedConfig] = useState(null);
  const [meta, setMeta] = useState({ groups: [], categories: [] });
  const [dataSources, setDataSources] = useState([]);
  const [sectionTemplates, setSectionTemplates] = useState([]);
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [showDataSources, setShowDataSources] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('builder');

  const savedEditorValue = useRef("");
  const debounceTimer = useRef(null);

  // Notification Helper
  const showNotification = useCallback((type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // API Functions
  const listConfigs = useCallback(async () => {
    try {
      setIsLoadingConfigs(true);
      const response = await axiosPrivate.get(
        `/dt/mchinfomod/admin/configs?group_code=${group_code}&project_code=${project_code}`
      );
      setConfigs(Array.isArray(response.data) ? response.data : response.data.configs || []);
    } catch (error) {
      console.error("Error loading configs:", error);
      showNotification("error", "설정 목록을 불러오는데 실패했습니다.");
    } finally {
      setIsLoadingConfigs(false);
    }
  }, [group_code, project_code, showNotification]);

  const getConfig = useCallback(async (configId) => {
    try {
      setIsLoadingConfig(true);
      const response = await axiosPrivate.get(
        `/dt/mchinfomod/admin/config/${configId}?group_code=${group_code}&project_code=${project_code}`
      );
      const config = response.data;
      const jsonStr = JSON.stringify(config.config_json, null, 2);
      setEditorValue(jsonStr);
      savedEditorValue.current = jsonStr;
      setDescription(config.description || "");
      setIsActive(config.is_active);
      setSelectedGroupId(config.group_id);
      setSelectedCategoryId(config.category_id);
      setParsedConfig(config.config_json);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error loading config:", error);
      showNotification("error", "설정을 불러오는데 실패했습니다.");
    } finally {
      setIsLoadingConfig(false);
    }
  }, [group_code, project_code, showNotification]);

  const getMeta = useCallback(async () => {
    try {
      const response = await axiosPrivate.get(
        `/dt/mchinfomod/admin/meta?group_code=${group_code}&project_code=${project_code}`
      );
      setMeta(response.data);
    } catch (error) {
      console.error("Error loading meta:", error);
    }
  }, [group_code, project_code]);

  const getDataSources = useCallback(async () => {
    try {
      const response = await axiosPrivate.get(
        `/dt/mchinfomod/admin/datasources?group_code=${group_code}&project_code=${project_code}`
      );
      setDataSources(Array.isArray(response.data) ? response.data : response.data.sources || []);
    } catch (error) {
      console.error("Error loading data sources:", error);
    }
  }, [group_code, project_code]);

  // Section Template API Functions (Phase 7)
  const getSectionTemplates = useCallback(async () => {
    try {
      const response = await axiosPrivate.get(
        `/dt/mchinfomod/admin/section-templates?group_code=${group_code}&project_code=${project_code}`
      );
      setSectionTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error loading section templates:", error);
    }
  }, [group_code, project_code]);

  const handleSaveAsTemplate = useCallback(async (section) => {
    const templateName = window.prompt("템플릿 이름을 입력하세요:", section.title || "새 템플릿");
    if (!templateName) return;

    try {
      await axiosPrivate.post(`/dt/mchinfomod/admin/section-template`, {
        group_code,
        project_code,
        template_name: templateName,
        section_json: section,
        description: `${section.title} (${(section.widgets || []).length} widgets)`,
      });
      showNotification("success", "섹션 템플릿이 저장되었습니다.");
      await getSectionTemplates();
    } catch (error) {
      console.error("Error saving section template:", error);
      showNotification("error", "템플릿 저장에 실패했습니다.");
    }
  }, [group_code, project_code, showNotification, getSectionTemplates]);

  const handleLoadTemplate = useCallback(async (templateId) => {
    try {
      const response = await axiosPrivate.get(
        `/dt/mchinfomod/admin/section-template/${templateId}?group_code=${group_code}&project_code=${project_code}`
      );
      const template = response.data;
      if (!template || !template.section_json) {
        showNotification("error", "템플릿 데이터를 불러올 수 없습니다.");
        return;
      }

      // Insert template section into config (give it a unique ID)
      const sectionData = { ...template.section_json };
      sectionData.id = `${sectionData.id || 'tpl'}_${Date.now()}`;

      const currentConfig = parsedConfig || { sections: [] };
      const newConfig = {
        ...currentConfig,
        sections: [...(currentConfig.sections || []), sectionData],
      };

      setParsedConfig(newConfig);
      const jsonStr = JSON.stringify(newConfig, null, 2);
      setEditorValue(jsonStr);
      setHasUnsavedChanges(jsonStr !== savedEditorValue.current);
      showNotification("success", `"${template.template_name}" 템플릿이 추가되었습니다.`);
    } catch (error) {
      console.error("Error loading section template:", error);
      showNotification("error", "템플릿 불러오기에 실패했습니다.");
    }
  }, [group_code, project_code, parsedConfig, showNotification]);

  const handleDeleteTemplate = useCallback(async (templateId) => {
    if (!window.confirm("이 템플릿을 삭제하시겠습니까?")) return;
    try {
      await axiosPrivate.delete(
        `/dt/mchinfomod/admin/section-template/${templateId}?group_code=${group_code}&project_code=${project_code}`
      );
      showNotification("success", "템플릿이 삭제되었습니다.");
      await getSectionTemplates();
    } catch (error) {
      console.error("Error deleting section template:", error);
      showNotification("error", "템플릿 삭제에 실패했습니다.");
    }
  }, [group_code, project_code, showNotification, getSectionTemplates]);

  const saveConfig = useCallback(async () => {
    // Validate JSON
    let configJson;
    try {
      configJson = JSON.parse(editorValue);
    } catch (error) {
      showNotification("error", "유효하지 않은 JSON 형식입니다.");
      return;
    }

    setIsSaving(true);
    try {
      if (selectedConfigId) {
        // Update existing - NOW includes group_id and category_id
        await axiosPrivate.put(`/dt/mchinfomod/admin/config/${selectedConfigId}`, {
          group_code,
          project_code,
          config_json: configJson,
          description,
          is_active: isActive,
          group_id: selectedGroupId,
          category_id: selectedCategoryId,
        });
        showNotification("success", "설정이 업데이트되었습니다.");
      } else {
        // Create new
        const response = await axiosPrivate.post(`/dt/mchinfomod/admin/config`, {
          group_code,
          project_code,
          group_id: selectedGroupId,
          category_id: selectedCategoryId,
          config_json: configJson,
          description
        });
        showNotification("success", "새 설정이 생성되었습니다.");
        await listConfigs();
        if (response.data.config_id) {
          setSelectedConfigId(response.data.config_id);
          await getConfig(response.data.config_id);
        }
      }
      savedEditorValue.current = editorValue;
      setHasUnsavedChanges(false);
      await listConfigs();
    } catch (error) {
      console.error("Error saving config:", error);
      showNotification("error", "설정 저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  }, [
    editorValue,
    selectedConfigId,
    description,
    isActive,
    selectedGroupId,
    selectedCategoryId,
    group_code,
    project_code,
    showNotification,
    listConfigs,
    getConfig
  ]);

  const deleteConfig = useCallback(async (configId) => {
    try {
      await axiosPrivate.delete(
        `/dt/mchinfomod/admin/config/${configId}?group_code=${group_code}&project_code=${project_code}`
      );
      showNotification("success", "설정이 삭제되었습니다.");
      await listConfigs();
      if (selectedConfigId === configId) {
        handleNewConfig();
      }
    } catch (error) {
      console.error("Error deleting config:", error);
      showNotification("error", "설정 삭제에 실패했습니다.");
    }
  }, [group_code, project_code, selectedConfigId, showNotification, listConfigs]);

  // Load initial data
  useEffect(() => {
    listConfigs();
    getMeta();
    getDataSources();
    getSectionTemplates();
  }, [listConfigs, getMeta, getDataSources, getSectionTemplates]);

  // Select Config Handler
  const handleSelectConfig = useCallback((configId) => {
    if (hasUnsavedChanges) {
      if (!window.confirm("저장하지 않은 변경사항이 있습니다. 계속하시겠습니까?")) {
        return;
      }
    }
    setSelectedConfigId(configId);
    getConfig(configId);
  }, [hasUnsavedChanges, getConfig]);

  // New Config Handler
  const handleNewConfig = useCallback(() => {
    if (hasUnsavedChanges) {
      if (!window.confirm("저장하지 않은 변경사항이 있습니다. 계속하시겠습니까?")) {
        return;
      }
    }
    setSelectedConfigId(null);
    const jsonStr = JSON.stringify(TEMPLATE_JSON, null, 2);
    setEditorValue(jsonStr);
    savedEditorValue.current = jsonStr;
    setDescription("");
    setIsActive(true);
    setSelectedGroupId(null);
    setSelectedCategoryId(null);
    setParsedConfig(TEMPLATE_JSON);
    setHasUnsavedChanges(false);
  }, [hasUnsavedChanges]);

  // Tab Switching with Bidirectional Sync
  const handleTabSwitch = useCallback((tab) => {
    if (tab === activeTab) return;
    
    if (tab === 'json') {
      // Builder → JSON: serialize current parsedConfig to string
      if (parsedConfig) {
        setEditorValue(JSON.stringify(parsedConfig, null, 2));
      }
    } else {
      // JSON → Builder: parse editorValue to config object
      try {
        const parsed = JSON.parse(editorValue || '{"sections":[]}');
        setParsedConfig(parsed);
      } catch (e) {
        // Invalid JSON — show notification, stay on JSON tab
        showNotification('error', 'JSON 구문 오류가 있어 빌더로 전환할 수 없습니다.');
        return;
      }
    }
    
    setActiveTab(tab);
  }, [activeTab, parsedConfig, editorValue, showNotification]);

  // Builder Change Handler
  const handleBuilderChange = useCallback((updatedConfig) => {
    setParsedConfig(updatedConfig);
    const jsonStr = JSON.stringify(updatedConfig, null, 2);
    setEditorValue(jsonStr);
    setHasUnsavedChanges(jsonStr !== savedEditorValue.current);
  }, []);

  // JSON Editor Change Handler with Debounced Preview
  const handleEditorChange = useCallback((value) => {
    setEditorValue(value || "");
    setHasUnsavedChanges(value !== savedEditorValue.current);

    // Debounced preview update
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      try {
        const parsed = JSON.parse(value || "{}");
        setParsedConfig(parsed);
      } catch (error) {
        setParsedConfig(null);
      }
    }, 300);
  }, []);

  // Import/Export Handlers
  const handleExport = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(editorValue);
      showNotification("success", "JSON이 클립보드에 복사되었습니다.");
    } catch (error) {
      showNotification("error", "클립보드 복사에 실패했습니다.");
    }
  }, [editorValue, showNotification]);

  const handleImport = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      JSON.parse(text); // Validate
      setEditorValue(text);
      setHasUnsavedChanges(text !== savedEditorValue.current);
      showNotification("success", "JSON을 가져왔습니다.");
    } catch (error) {
      if (error instanceof SyntaxError) {
        showNotification("error", "유효하지 않은 JSON 형식입니다.");
      } else {
        showNotification("error", "클립보드에서 가져오기 실패했습니다.");
      }
    }
  }, [showNotification]);

  // Copy to clipboard helper for data sources
  const handleCopySourceKey = useCallback(async (sourceKey) => {
    try {
      await navigator.clipboard.writeText(sourceKey);
      showNotification("success", `${sourceKey} 복사됨`);
    } catch (error) {
      showNotification("error", "복사 실패");
    }
  }, [showNotification]);

  // Loading Screen
  if (!isTailwindReady || isLoadingConfigs) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
          <p className="mt-4 text-slate-400">로딩 중...</p>
        </div>
      </div>
    );
  }

  const isJsonInvalid = editorValue && !parsedConfig;

  return (
    <div className="h-screen flex flex-col bg-[#0a0e1a] text-slate-200">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h1 className="text-xl font-black text-slate-100 tracking-tight uppercase mb-1">
              패널 설정 편집기
            </h1>
            <input
              type="text"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setHasUnsavedChanges(true);
              }}
              placeholder="설정 설명 입력..."
              className="w-full max-w-md bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <span className={isActive ? "text-emerald-400" : "text-slate-500"}>
                {isActive ? "활성" : "비활성"}
              </span>
              <button
                onClick={() => {
                  setIsActive(!isActive);
                  setHasUnsavedChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isActive ? "bg-emerald-500" : "bg-slate-700"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isActive ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </label>

            {/* Group/Category selectors — ALWAYS visible */}
            <select
              value={selectedGroupId || ""}
              onChange={(e) => {
                setSelectedGroupId(e.target.value || null);
                setHasUnsavedChanges(true);
              }}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">그룹 없음 (전체)</option>
              {meta.groups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>

            <select
              value={selectedCategoryId || ""}
              onChange={(e) => {
                setSelectedCategoryId(e.target.value || null);
                setHasUnsavedChanges(true);
              }}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">카테고리 없음 (전체)</option>
              {meta.categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            <button
              onClick={handleImport}
              className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg border border-slate-700 transition-colors"
            >
              📋 가져오기
            </button>

            <button
              onClick={handleExport}
              className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg border border-slate-700 transition-colors"
            >
              📤 내보내기
            </button>

            <button
              onClick={() => setShowDataSources(!showDataSources)}
              className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg border border-slate-700 transition-colors"
            >
              📊 데이터 소스
            </button>

            <button
              onClick={saveConfig}
              disabled={isSaving || isJsonInvalid}
              className={`px-6 py-1.5 text-sm font-bold rounded-lg transition-all ${
                isSaving || isJsonInvalid
                  ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/30"
              }`}
            >
              {isSaving ? "저장 중..." : "💾 저장"}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Config List (280px) */}
        <ConfigListPanel
          configs={configs}
          selectedConfigId={selectedConfigId}
          onSelectConfig={handleSelectConfig}
          onNewConfig={handleNewConfig}
          onDeleteConfig={deleteConfig}
          meta={meta}
        />

        {/* Center Panel - Builder or JSON */}
        <div className="flex-1 flex flex-col bg-slate-950 border-r border-slate-800">
          {/* Tab Header */}
          <div className="flex items-center border-b border-slate-800 bg-slate-900/80">
            <button
              onClick={() => handleTabSwitch('builder')}
              className={`px-4 py-2.5 text-sm font-bold transition-colors ${
                activeTab === 'builder'
                  ? 'text-indigo-400 border-b-2 border-indigo-400'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              🔧 빌더
            </button>
            <button
              onClick={() => handleTabSwitch('json')}
              className={`px-4 py-2.5 text-sm font-bold transition-colors ${
                activeTab === 'json'
                  ? 'text-indigo-400 border-b-2 border-indigo-400'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              {`{ }`} JSON
            </button>
          </div>
          
          {/* Tab Content */}
          {activeTab === 'builder' ? (
            <BuilderPanel
              config={parsedConfig || { sections: [] }}
              onChange={handleBuilderChange}
              dataSources={dataSources}
              sectionTemplates={sectionTemplates}
              onSaveAsTemplate={handleSaveAsTemplate}
              onLoadTemplate={handleLoadTemplate}
              onDeleteTemplate={handleDeleteTemplate}
              onRefreshDataSources={getDataSources}
            />
          ) : (
            <JsonEditorPanel
              value={editorValue}
              onChange={handleEditorChange}
              isLoading={isLoadingConfig}
              isInvalid={isJsonInvalid}
            />
          )}
        </div>

        {/* Right Panel - Preview (380px) */}
        <PreviewPanel
          parsedConfig={parsedConfig}
          isJsonInvalid={activeTab === 'json' && isJsonInvalid}
          dataSources={dataSources}
          axiosPrivate={axiosPrivate}
          groupCode={group_code}
          projectCode={project_code}
        />
      </div>

      {/* Data Source Drawer */}
      {showDataSources && (
        <DataSourceDrawer
          dataSources={dataSources}
          onClose={() => setShowDataSources(false)}
          onCopyKey={handleCopySourceKey}
          onRefreshDataSources={getDataSources}
          axiosPrivate={axiosPrivate}
          groupCode={group_code}
          projectCode={project_code}
        />
      )}

      {/* Notification Toast */}
      {notification && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-right">
          <div
            className={`px-6 py-3 rounded-lg shadow-2xl flex items-center gap-3 ${
              notification.type === "success"
                ? "bg-emerald-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {notification.type === "success" ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="font-medium text-sm">{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}
